import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { BUCKET_STYLE } from "@/lib/format";
import { usePullToRefresh } from "@/hooks/usePullToRefresh";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  CalendarClock, AlertTriangle, ArrowRight, Sparkles, Camera, Mic,
  TrendingUp, Trophy, AudioLines,
} from "lucide-react";
import { toast } from "sonner";
import { StatTile, FollowupRow, greetingForNow } from "./_shared";
import { fmtINR } from "@/lib/format";

/**
 * SalespersonDashboard — Action-oriented view for sales reps.
 * Focus: today's follow-ups, quick capture, nothing analytic-heavy.
 */
export default function SalespersonDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [board, setBoard] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const [home, lb] = await Promise.all([
        api.get("/dashboard/home"),
        api.get("/admin/team/leaderboard").catch(() => ({ data: null })),
      ]);
      setData(home.data);
      setBoard(lb.data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);
  const { indicator: pullIndicator } = usePullToRefresh(load);

  const completeFu = async (id) => {
    try {
      await api.post(`/followups/${id}/complete`);
      toast.success("Follow-up completed");
      load();
    } catch {
      toast.error("Could not complete");
    }
  };

  if (loading) {
    return (
      <div className="p-6 sm:p-8 space-y-6">
        <Skeleton className="h-12 w-2/3 rounded-2xl" />
        <div className="grid grid-cols-3 gap-4">
          <Skeleton className="h-28 rounded-3xl" />
          <Skeleton className="h-28 rounded-3xl" />
          <Skeleton className="h-28 rounded-3xl" />
        </div>
        <Skeleton className="h-64 rounded-3xl" />
      </div>
    );
  }

  const firstName = (user?.name || user?.email || "").split(/\s+/)[0];

  return (
    <div className="p-4 sm:p-8 lg:p-10 space-y-6 sm:space-y-8 max-w-[1180px] mx-auto" data-testid="dashboard-salesperson">
      {pullIndicator}
      <header className="space-y-1.5 animate-fade-in-up">
        <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-muted-foreground">{greetingForNow()}</p>
        <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tight">
          {firstName}<span className="text-primary">.</span>
        </h1>
        <p className="text-sm text-muted-foreground max-w-md">
          Your day, at a glance. Close the loop on follow-ups before anything else.
        </p>
      </header>

      <section className="grid grid-cols-3 gap-2 sm:gap-4">
        <StatTile label="Overdue" value={data.overdue_count} hint="Take action today" accent="text-red-600" icon={AlertTriangle} to="/followups?bucket=overdue" />
        <StatTile label="Due today" value={data.today_count} hint="Don't miss these" accent="text-amber-600" icon={CalendarClock} to="/followups?bucket=today" />
        <StatTile label="Upcoming" value={data.upcoming_count} hint="Plan your week" accent="text-primary-600" icon={TrendingUp} to="/followups?bucket=upcoming" />
      </section>

      <section className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <Link to="/ai-extract?tab=voice-journal" data-testid="quick-voice-journal" className="group p-4 rounded-3xl bg-primary text-primary-foreground tap card-lift shadow-soft">
          <AudioLines className="h-5 w-5" />
          <p className="font-semibold mt-2 text-sm">Record voice journal</p>
          <p className="text-[11px] opacity-80 mt-0.5">Talk · AI logs everything</p>
        </Link>
        <Link to="/ai-extract?tab=meeting" data-testid="quick-meeting" className="group p-4 rounded-3xl bg-card border border-border/60 tap card-lift shadow-soft">
          <Mic className="h-5 w-5 text-primary" />
          <p className="font-semibold mt-2 text-sm">Add meeting update</p>
          <p className="text-[11px] text-muted-foreground mt-0.5">Paste summary → AI extracts</p>
        </Link>
        <Link to="/ai-extract?tab=vcard" data-testid="quick-vcard" className="group p-4 rounded-3xl bg-card border border-border/60 tap card-lift shadow-soft">
          <Camera className="h-5 w-5 text-primary" />
          <p className="font-semibold mt-2 text-sm">Upload visiting card</p>
          <p className="text-[11px] text-muted-foreground mt-0.5">OCR contact details</p>
        </Link>
        <Link to="/deals?new=1" data-testid="quick-deal" className="group p-4 rounded-3xl bg-card border border-border/60 tap card-lift shadow-soft">
          <Sparkles className="h-5 w-5 text-primary" />
          <p className="font-semibold mt-2 text-sm">Add deal</p>
          <p className="text-[11px] text-muted-foreground mt-0.5">New opportunity</p>
        </Link>
        <Link to="/pipeline" data-testid="quick-pipeline" className="group p-4 rounded-3xl bg-card border border-border/60 tap card-lift shadow-soft">
          <ArrowRight className="h-5 w-5 text-primary" />
          <p className="font-semibold mt-2 text-sm">Open pipeline</p>
          <p className="text-[11px] text-muted-foreground mt-0.5">All stages</p>
        </Link>
      </section>

      {/* Sales Pulse leaderboard — visible to all sales people so they can
          see where they stand and feel the pull of the podium. */}
      {board && (board.rows || []).length > 0 && (
        <section data-testid="sales-leaderboard">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Trophy className="h-4 w-4 text-primary" />
              <h2 className="font-display text-lg sm:text-xl font-extrabold">Sales Pulse · this month</h2>
            </div>
            <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground">Top 3</span>
          </div>
          <div className="grid grid-cols-3 gap-2 sm:gap-4">
            {(board.rows || []).slice(0, 3).map((r, i) => {
              const isMe = r.id === user?.id;
              return (
                <div
                  key={r.id}
                  data-testid={`sales-podium-${i + 1}`}
                  className={`p-3 sm:p-5 rounded-3xl border-2 bg-gradient-to-br to-card overflow-hidden ${isMe ? "ring-2 ring-primary ring-offset-2" : ""}`}
                  style={{ borderColor: i === 0 ? "#fcd34d" : i === 1 ? "#d4d4d8" : "#fdba74" }}
                >
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <Trophy className={`h-3 w-3 ${i === 0 ? "text-yellow-600" : i === 1 ? "text-zinc-500" : "text-orange-600"}`} />
                    <span className="text-[9px] font-bold uppercase tracking-[0.14em] text-muted-foreground">#{i + 1}</span>
                    {isMe && <span className="text-[9px] font-bold uppercase tracking-[0.14em] text-primary ml-auto">You</span>}
                  </div>
                  <p className="text-xs font-bold truncate">{r.name || r.email}</p>
                  <p className="font-display text-base sm:text-2xl font-black tracking-tight mt-1 leading-none truncate">{fmtINR(r.won_value)}</p>
                  <p className="text-[10px] text-muted-foreground mt-0.5">{r.won_count} deal{r.won_count !== 1 ? "s" : ""}</p>
                </div>
              );
            })}
            {(board.rows || []).length < 3 && Array.from({ length: 3 - (board.rows || []).length }).map((_, i) => (
              <div key={`pe-${i}`} className="p-3 sm:p-5 rounded-3xl border-2 border-dashed border-border/60 text-center text-xs text-muted-foreground grid place-items-center min-h-[88px]">—</div>
            ))}
          </div>
          {/* "Where am I?" rank chip if user isn't in the podium */}
          {(() => {
            const myRank = (board.rows || []).findIndex((r) => r.id === user?.id);
            if (myRank > 2) {
              const me = board.rows[myRank];
              return (
                <p className="mt-3 text-xs text-center text-muted-foreground" data-testid="sales-my-rank">
                  Your rank: <span className="font-bold text-foreground">#{myRank + 1}</span> · {fmtINR(me.won_value)} won
                </p>
              );
            }
            return null;
          })()}
        </section>
      )}

      <section className="grid lg:grid-cols-3 gap-5">
        {[
          { key: "overdue", title: "Overdue", rows: data.overdue, empty: "All caught up. Nothing overdue." },
          { key: "today", title: "Due today", rows: data.today, empty: "Nothing due today." },
          { key: "upcoming", title: "Upcoming", rows: data.upcoming, empty: "No upcoming follow-ups." },
        ].map((sec) => {
          const s = BUCKET_STYLE[sec.key];
          return (
            <Card key={sec.key} className="rounded-3xl border-border/60 shadow-soft overflow-hidden" data-testid={`section-${sec.key}`}>
              <CardContent className="p-4 sm:p-5">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <span className={`h-2 w-2 rounded-full ${s.dot}`} />
                    <h3 className="font-display text-lg font-extrabold">{sec.title}</h3>
                  </div>
                  <Badge variant="outline" className={`${s.chip} text-[10px] font-bold uppercase tracking-wider`}>
                    {sec.rows.length}
                  </Badge>
                </div>
                <div className="space-y-2">
                  {sec.rows.length === 0 ? (
                    <p className="text-sm text-muted-foreground py-6 text-center">{sec.empty}</p>
                  ) : (
                    sec.rows.slice(0, 6).map((fu) => <FollowupRow key={fu.id} fu={fu} onComplete={completeFu} onPing={load} />)
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </section>
    </div>
  );
}
